/**
 * The icon set.
 *
 * Before this file the dashboard had no icons and two impostors: a 🔔 emoji in
 * the topbar, which renders as a different drawing on every operating system
 * and lands full-colour on a page with one accent, and `‹ ›` typographic
 * quotes doing duty as the day board's date arrows. Both are the same mistake —
 * a glyph from a text font standing in for a drawn mark — and both are why the
 * navigation had to be a wall of words.
 *
 * Every icon here is drawn to the same specification as `BrandMark`, because
 * the mark is the only piece of Laseera artwork that already existed and the
 * set has to look like it came from the same hand:
 *
 * * a 24-unit box, so a 20px and a 24px icon share one grid;
 * * one stroke weight, 1.7, with round caps and round joins — the mark's
 *   `strokeLinecap="round"` and its 3.4-on-40 weight, which is 2.04 on 24 and
 *   is walked down to 1.7 because the mark is a logo and these are furniture;
 * * `currentColor`, never a fill, so an icon takes the ink of whatever it sits
 *   in and needs no reversed variant for the orchid nav row or the dark banner;
 * * no icon carries meaning alone. Every one in this product ships beside its
 *   label, and each is `aria-hidden` unless given a title, so a screen reader
 *   hears the label once rather than the label and its picture.
 *
 * `vector-effect: non-scaling-stroke` is deliberately absent: these are used at
 * 16–24px where the stroke should scale with the box, and pinning it makes an
 * 18px icon read heavier than the 20px one next to it.
 */

export type IconName =
  // Navigation
  | 'board'
  | 'patients'
  | 'worklists'
  | 'checkout'
  | 'chat'
  | 'catalog'
  | 'packages'
  | 'policies'
  | 'staff'
  | 'branches'
  | 'branding'
  | 'messages'
  | 'consents'
  | 'aftercare'
  | 'machines'
  | 'inventory'
  | 'key'
  | 'sales'
  | 'expenses'
  | 'refunds'
  | 'statements'
  | 'reports'
  | 'broadcasts'
  | 'audit'
  | 'clinics'
  | 'invoices'
  | 'flags'
  | 'support'
  // Interface
  | 'bell'
  | 'search'
  | 'close'
  | 'check'
  | 'alert'
  | 'info'
  | 'plus'
  | 'more'
  | 'download'
  | 'external'
  | 'chevronDown'
  | 'chevronEnd'
  | 'chevronStart'
  | 'arrowEnd'
  | 'clock'
  | 'calendar'
  | 'menu'
  | 'signOut'
  | 'switch'
  | 'live'
  | 'trendUp'
  | 'trendDown';

/**
 * The drawings, as path data on a 24-unit box.
 *
 * Kept as a flat record of strings rather than as JSX so the set can be
 * iterated, so no icon can accidentally acquire a fill or a second stroke
 * weight, and so adding one is adding a line.
 */
const PATHS: Readonly<Record<IconName, string>> = {
  // ── Navigation ──────────────────────────────────────────────────────────
  // Three columns under a rail: the day board is columns of doctors, and this
  // is the only nav icon that draws its own screen.
  board: 'M3 6h18M3 6v13a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V6M9 10v10M15 10v10',
  patients: 'M16 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 18.5V20M10 12a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M20 20v-1.4a3.5 3.5 0 0 0-2.6-3.4M15.5 5.2a3.5 3.5 0 0 1 0 6.6',
  worklists: 'M9 6h11M9 12h11M9 18h11M4 6l1.2 1.2L7.5 4.8M4 12l1.2 1.2L7.5 10.8M4 18l1.2 1.2L7.5 16.8',
  checkout: 'M4 4.8v15.4l2.4-1.5 2.4 1.5 2.4-1.5 2.4 1.5 2.4-1.5 2.4 1.5V4.8a1.8 1.8 0 0 0-1.8-1.8H5.8A1.8 1.8 0 0 0 4 4.8ZM8 8.5h8M8 12.5h5',
  chat: 'M20.5 12.2c0 3.9-3.8 7-8.5 7a9.9 9.9 0 0 1-2.6-.34L4 20.5l1.3-3.6A6.6 6.6 0 0 1 3.5 12.2c0-3.9 3.8-7 8.5-7s8.5 3.1 8.5 7Z',
  catalog: 'M20.2 12.9 12.9 20.2a1.8 1.8 0 0 1-2.5 0l-6.6-6.6a1.8 1.8 0 0 1-.5-1.3V5a1.8 1.8 0 0 1 1.8-1.8h7.3c.5 0 .9.2 1.3.5l6.5 6.6a1.8 1.8 0 0 1 0 2.6ZM7.8 7.8h.01',
  packages: 'M3.6 7.6 12 12l8.4-4.4M12 12v8.6M20.4 7.9v8.2a1.4 1.4 0 0 1-.75 1.24l-6.9 3.6a1.5 1.5 0 0 1-1.4 0l-6.9-3.6A1.4 1.4 0 0 1 3.6 16V7.9a1.4 1.4 0 0 1 .75-1.24l6.9-3.6a1.5 1.5 0 0 1 1.4 0l6.9 3.6A1.4 1.4 0 0 1 20.4 7.9ZM7.8 5.1l8.4 4.4',
  policies: 'M12 3.2 4.6 6v6c0 4.2 3 7.6 7.4 8.8 4.4-1.2 7.4-4.6 7.4-8.8V6ZM9 11.9l2.1 2.1 4-4',
  staff: 'M15 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-4A3.5 3.5 0 0 0 4 18.5V20M9.5 11.6a3.4 3.4 0 1 0 0-6.8 3.4 3.4 0 0 0 0 6.8M18 6.5v5M15.5 9h5',
  branches: 'M4 20.2h16M5.6 20.2V6.1a1.5 1.5 0 0 1 1-1.4l4.9-1.6a1.5 1.5 0 0 1 2 1.4v15.7M18.4 20.2v-8.6a1.5 1.5 0 0 0-1.1-1.44L13.5 9M8.6 8.3h1.8M8.6 12h1.8M8.6 15.7h1.8',
  branding: 'M12 3.4a8.6 8.6 0 0 0 0 17.2c1.2 0 2-.8 2-1.9 0-.5-.2-.95-.5-1.3-.3-.34-.5-.78-.5-1.26 0-1.05.85-1.9 1.9-1.9h1.7a3.9 3.9 0 0 0 3.9-3.9c0-3.9-3.8-6.94-8.5-6.94ZM7.6 12.4h.01M9.4 8.5h.01M14.3 7.9h.01M17.6 11h.01',
  messages: 'M3.4 7.4a2 2 0 0 1 2-2h13.2a2 2 0 0 1 2 2v9.2a2 2 0 0 1-2 2H5.4a2 2 0 0 1-2-2ZM3.7 7.6l7.2 5a2 2 0 0 0 2.2 0l7.2-5',
  consents: 'M14 3.4H6.6a1.8 1.8 0 0 0-1.8 1.8v13.6a1.8 1.8 0 0 0 1.8 1.8h10.8a1.8 1.8 0 0 0 1.8-1.8V8.4ZM14 3.4V8.4h5.2M8.4 16.4c1.4 0 1.4-2.4 2.8-2.4s1.4 2.4 2.8 2.4 1.4-1.2 2.4-1.2',
  aftercare: 'M12 20.2s-7.4-4.2-7.4-9.3A4.3 4.3 0 0 1 12 8.1a4.3 4.3 0 0 1 7.4 2.8c0 5.1-7.4 9.3-7.4 9.3ZM12 11.6v4M10 13.6h4',
  machines: 'M8.4 3.4h7.2M12 3.4v3.4M6.2 6.8h11.6a1.8 1.8 0 0 1 1.8 1.8v10a1.8 1.8 0 0 1-1.8 1.8H6.2a1.8 1.8 0 0 1-1.8-1.8v-10a1.8 1.8 0 0 1 1.8-1.8ZM12.8 10.4l-2.6 3.9h3.6l-2.6 3.9',
  inventory: 'M4 8.6h16M4 8.6V18a1.6 1.6 0 0 0 1.6 1.6h12.8A1.6 1.6 0 0 0 20 18V8.6M4 8.6l1.9-3.4a1.6 1.6 0 0 1 1.4-.8h9.4a1.6 1.6 0 0 1 1.4.8L20 8.6M9.6 12.6h4.8',
  key: 'M14.6 9.4a3.6 3.6 0 1 1 5.1 5.1 3.6 3.6 0 0 1-5.1-5.1ZM14.6 14.5 8.2 20.9l-2.6-.5-.5-2.6 1.1-1.1 1.6.3.3-1.9 1.9-.3-.3-1.6Z',
  sales: 'M3.6 7.2a1.6 1.6 0 0 1 1.6-1.6h13.6a1.6 1.6 0 0 1 1.6 1.6v9.6a1.6 1.6 0 0 1-1.6 1.6H5.2a1.6 1.6 0 0 1-1.6-1.6ZM12 14.5a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5M6.9 9.2h.01M17.1 14.8h.01',
  expenses: 'M12 4.6v10.8M8.2 11.6 12 15.4l3.8-3.8M4.6 19.4h14.8',
  refunds: 'M4.4 10.4a7.8 7.8 0 1 1 .6 5M4.4 5.4v5h5',
  statements: 'M14 3.4H6.6a1.8 1.8 0 0 0-1.8 1.8v13.6a1.8 1.8 0 0 0 1.8 1.8h10.8a1.8 1.8 0 0 0 1.8-1.8V8.4ZM14 3.4V8.4h5.2M8.6 12.6h6.8M8.6 16.4h4.4',
  reports: 'M4.4 19.6h15.2M7.6 19.6v-5.8M12 19.6V7.4M16.4 19.6v-8.4',
  broadcasts: 'M4.4 9.4v4.6a1.4 1.4 0 0 0 1.4 1.4h2l6.6 4.2a1 1 0 0 0 1.5-.85V5.25a1 1 0 0 0-1.5-.85L7.8 8.6h-2a1.4 1.4 0 0 0-1.4 1.4ZM7.8 15.4v4M19.4 10.2a2.8 2.8 0 0 1 0 3.6',
  audit: 'M12 3.2 4.6 6v6c0 4.2 3 7.6 7.4 8.8 4.4-1.2 7.4-4.6 7.4-8.8V6ZM12 8.2v4l2.4 1.6',
  clinics: 'M3.4 20.2h17.2M5.4 20.2V4.8a1.4 1.4 0 0 1 1.4-1.4h5.6a1.4 1.4 0 0 1 1.4 1.4v15.4M13.8 20.2v-9h3.4a1.4 1.4 0 0 1 1.4 1.4v7.6M8.4 7.4h2.4M8.4 11.2h2.4M8.4 15h2.4',
  invoices: 'M6.4 3.4h11.2a1.4 1.4 0 0 1 1.4 1.4v15.8l-2.6-1.6-2.2 1.6-2.2-1.6-2.2 1.6-2.2-1.6L5 20.6V4.8a1.4 1.4 0 0 1 1.4-1.4ZM8.8 8h6.4M8.8 12h4.2',
  flags: 'M5.4 20.4V4.6M5.4 5.4h10.4l-1.6 3.2 1.6 3.2H5.4M18.6 15.4a2.6 2.6 0 1 0 0-5.2 2.6 2.6 0 0 0 0 5.2',
  support: 'M12 20.4a8.4 8.4 0 1 0 0-16.8 8.4 8.4 0 0 0 0 16.8ZM12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4M9.75 9.75 6.05 6.05M14.25 9.75l3.7-3.7M14.25 14.25l3.7 3.7M9.75 14.25l-3.7 3.7',

  // ── Interface ───────────────────────────────────────────────────────────
  bell: 'M18 8.8a6 6 0 1 0-12 0c0 5-2 6.4-2 6.4h16s-2-1.4-2-6.4ZM13.7 19a2 2 0 0 1-3.4 0',
  search: 'M11 18a7 7 0 1 0 0-14 7 7 0 0 0 0 14ZM20 20l-4.05-4.05',
  close: 'M6.4 6.4l11.2 11.2M17.6 6.4 6.4 17.6',
  check: 'M5 12.6 9.6 17 19 6.6',
  alert: 'M12 8.6v4.6M12 16.6h.01M10.3 4.3 3.1 17a2 2 0 0 0 1.7 3h14.4a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0Z',
  info: 'M12 20.4a8.4 8.4 0 1 0 0-16.8 8.4 8.4 0 0 0 0 16.8ZM12 11.4v5M12 7.8h.01',
  plus: 'M12 5.4v13.2M5.4 12h13.2',
  more: 'M6 12h.01M12 12h.01M18 12h.01',
  download: 'M12 3.8v11M7.6 10.4 12 14.8l4.4-4.4M4.6 19.4h14.8',
  external: 'M14 4.6h5.4V10M19.4 4.6 11 13M17 14.2v4.2a1.4 1.4 0 0 1-1.4 1.4H5.6a1.4 1.4 0 0 1-1.4-1.4V8.4A1.4 1.4 0 0 1 5.6 7h4.2',
  chevronDown: 'M6.4 9.4 12 15l5.6-5.6',
  chevronEnd: 'M9.4 5.4 16 12l-6.6 6.6',
  chevronStart: 'M14.6 5.4 8 12l6.6 6.6',
  arrowEnd: 'M4.6 12h14.2M13.4 6.6 18.8 12l-5.4 5.4',
  clock: 'M12 20.4a8.4 8.4 0 1 0 0-16.8 8.4 8.4 0 0 0 0 16.8ZM12 7.4V12l3 1.9',
  calendar: 'M5.6 6.2h12.8a1.4 1.4 0 0 1 1.4 1.4v11a1.4 1.4 0 0 1-1.4 1.4H5.6a1.4 1.4 0 0 1-1.4-1.4v-11a1.4 1.4 0 0 1 1.4-1.4ZM8.4 3.8v4.4M15.6 3.8v4.4M4.2 11.4h15.6',
  menu: 'M4 7h16M4 12h16M4 17h16',
  signOut: 'M9.6 19.4H6a1.6 1.6 0 0 1-1.6-1.6V6.2A1.6 1.6 0 0 1 6 4.6h3.6M15 15.6 19.4 12 15 8.4M19.4 12H9.2',
  switch: 'M6.6 8.4h12.8M16.2 5.2l3.2 3.2-3.2 3.2M17.4 15.6H4.6M7.8 12.4l-3.2 3.2 3.2 3.2',
  // Not a dot but a broadcast arc: the day board's live indicator, which has to
  // read as "receiving" and not as a bullet point.
  live: 'M12 13.4a1.4 1.4 0 1 0 0-2.8 1.4 1.4 0 0 0 0 2.8M8.5 15.5a5 5 0 0 1 0-7M15.5 8.5a5 5 0 0 1 0 7M5.7 18.3a9 9 0 0 1 0-12.6M18.3 5.7a9 9 0 0 1 0 12.6',
  trendUp: 'M3.6 16.4 9.4 10.6l3.4 3.4 7.6-7.6M14.6 6.4h5.8v5.8',
  trendDown: 'M3.6 7.6 9.4 13.4l3.4-3.4 7.6 7.6M14.6 17.6h5.8v-5.8',
};

export interface IconProps {
  readonly name: IconName;
  /** Rendered edge length in pixels. 20 in a nav row, 18 in a button, 16 inline. */
  readonly size?: number;
  /**
   * Supplied only when the icon is the whole control — a bare icon button.
   * Everywhere else it sits beside its own label and stays hidden, because a
   * screen reader announcing "Patients Patients" is worse than announcing it
   * once.
   */
  readonly title?: string;
  readonly className?: string;
}

export function Icon({ name, size = 20, title, className }: IconProps) {
  return (
    <svg
      className={className ? `icon ${className}` : 'icon'}
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      focusable="false"
    >
      {title ? <title>{title}</title> : null}
      <path d={PATHS[name]} />
    </svg>
  );
}
